<template>
    <MainContentSearchable 
        title="Parcelas vendidas" 
        id="parcelas" 
        v-model="filters"
        :meta="loading ? {} : listadoParcelas.meta" 
        :disabled="loading" 
        @updated="debounceGetListadoParcelas"
    >
        <template v-slot:header>
            <h3 class="card-title pb-3">Listado de parcelas vendidas</h3>
        </template>

        <template v-slot:extra-options>
            <div class="col-12 col-lg-9 pr-0 mr-0">
                <div class="input-group-sm my-3">
                    <select class="custom-select w-auto mr-2" id="search" v-model="filters.search_for" @change="debounceGetListadoParcelas()">
                        <option value="">Buscar por</option>
                        <option value="n_pedido">Nº Pedido</option>
                        <option value="name">Nombre</option>
                        <option value="email">Email</option>
                    </select>

                    <select class="custom-select w-auto mr-2" v-model="filters.order_by" @change="sortChanged()">
                        <option value="">Ordenar por</option>
                        <option value="id">Nº Pedido</option>
                        <option value="created_at">Fecha</option>
                        <option value="price">Precio</option>
                    </select>

                    <select class="custom-select w-auto mr-2" v-model="filters.sort" @change="sortChanged()">
                        <option value="asc">Ascendente</option>
                        <option value="desc">Descendente</option>
                    </select>
                </div>
            </div>
        </template>

        <template v-slot:content>
            <ContainerTable :data="listadoParcelas.data || []" :responsive="false">
                <b-table 
                    hover 
                    :items="listadoParcelas.data || []" 
                    :fields="fields" 
                    :busy="loading"
                    empty-text="No hay parcelas vendidas"
                    responsive
                    :row-details="true"
                >
                    <template #table-busy>
                        <div class="text-center text-danger my-2">
                            <ContentOrLoading 
                                style="height: 100px" 
                            />
                        </div>
                    </template>

                    <template #cell(n_pedido)="{ item }">
                        {{ item.n_pedido }}
                    </template>

                    <template #cell(price_total_pedido)="{ item }">
                        {{ item.price_total_pedido | formatear_precio }} 
                    </template>

                    <template #cell(taquilla)="{ item }">
                        <span v-if="item.taquilla">Taquilla</span>
                        <span v-else>Online</span>
                    </template>

                    <template #cell(user_name)="{ item }">
                        {{ item.parcelas[0].user.name }}
                    </template>

                    <template #cell(user_email)="{ item }">
                        {{ item.parcelas[0].user.email }}
                    </template>

                    <template #cell(fecha_compra)="{ item }">
                        {{ item.fecha_compra }}
                    </template>

                    <template #cell(acciones)="{ toggleDetails }">
                        <b-button size="sm" @click="toggleDetails" variant="primary">
                            Ver parcelas
                        </b-button>
                    </template>

                    <template #row-details="{ item }">
                        <b-table 
                            :items="item.parcelas" 
                            :fields="parcelasFields" 
                            small
                            responsive
                        >
                            <template #cell(parcela_numero)="{ value }">
                                {{ value }}
                            </template>
                            <template #cell(zona)="{ value }">
                                {{ value.nombre }}
                            </template>
                            <template #cell(parcela_ticket)="{ value }">
                                {{ value.price | formatear_precio }}
                            </template>
                        </b-table>
                    </template>
                </b-table>
            </ContainerTable>
        </template>
    </MainContentSearchable>
</template>

<script>
    import MainContentSearchable from '@/components/backend/components/MainContentSearchable';
    import ContainerTable from '@/components/backend/common/ContainerTable';
    import ContentOrLoading from '@/components/backend/components//ContentOrLoading';
    import _ from 'lodash';

    export default {
        components: {
            MainContentSearchable, ContainerTable, ContentOrLoading
        },
        data() {
            return {
                loading: true,
                listadoParcelas: {
                    data: [],
                    meta: {},
                }, 
                filters: {
                    page: 1,
                    per_page: 10,
                    search_for: 'name',
                    order_by: '',
                    sort: 'asc',
                    search: '',
                },
                fields: [
                    { key: 'n_pedido', label: 'N° Pedido', class: 'text-center' },
                    { key: 'price_total_pedido', label: 'Total Pedido', class: 'text-center' },
                    { key: 'taquilla', label: 'Modalidad', class: 'text-center' },
                    { key: 'user_name', label: 'Usuario', class: 'text-center' },
                    { key: 'user_email', label: 'Email', class: 'text-center' },
                    { key: 'fecha_compra', label: 'Fecha Compra', class: 'text-center' },
                    { key: 'acciones', label: '', class: 'text-center' }
                ],
                parcelasFields: [
                    { key: 'parcela_numero', label: 'Número Parcela', class: 'text-center' },
                    { key: 'zona', label: 'Zona', class: 'text-center' },
                    { key: 'parcela_ticket', label: 'Precio', class: 'text-center' }
                ]
            };
        },
        methods: {
            async getListadoParcelas() {
                if (!this.$store.getters.getListaAbonados?.id) {
                    this.$toast.error("No se ha encontrado una lista de abonados");
                    return;
                }
                
                try {
                    this.loading = true;
                    const response = await this.$store.dispatch('BACK_LISTADO_PARCELAS_VENDIDAS', this.searchParams());
                    this.listadoParcelas = response.data;
                } 
                catch (error) {
                    this.$toast.error("Error al obtener el listado de parcelas: " + (error.message || ""));
                    this.listadoParcelas = { data: [] };
                }
                finally {
                    this.loading = false;
                }
            },
            searchParams() {
                const params = {
                    lista_id: this.$store.getters.getListaAbonados?.id,
                    filters: {
                        per_page: this.filters.per_page,
                        page: this.filters.page
                    }
                };
                
                if (this.filters.search) {
                    params.filters.search = this.filters.search;
                }
                
                if (this.filters.search_for) {
                    params.filters.search_for = this.filters.search_for;
                }

                if (this.filters.order_by) {
                    params.filters.order_by = this.filters.order_by;
                }

                if (this.filters.sort) {
                    params.filters.sort = this.filters.sort;
                }

                return params;
            },


            sortChanged() {
                this.updateURL();
            },

            updateURL() {
                let query = {
                    per_page: this.filters.per_page,
                    page: this.filters.page
                };

                if (this.filters.search_for) {
                    query.search_for = this.filters.search_for;
                }

                if (this.filters.search) {
                    query.search = this.filters.search;
                }

                if (this.filters.order_by) {
                    query.order_by = this.filters.order_by;
                }

                if (this.filters.sort) {
                    query.sort = this.filters.sort;
                }

                this.$router.push({ 
                    path: this.$route.path, 
                    query: query 
                }).finally(() => this.getListadoParcelas());
            }
        },
        created() {
            this.debounceGetListadoParcelas = _.debounce(this.updateURL, 500);
            this.getListadoParcelas();
        }
    };
</script>